import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/primitives/Button";
import { Input } from "../../components/primitives/Input";
import { Label } from "../../components/primitives/Label";
import { Icon } from "../../components/primitives/Icon";
import { AppLogo } from "../../components/brand/AppLogo";
import { t } from "../../i18n";
const MIN_LENGTH = 8;
/**
 * Blocking, full-screen "you must change your password" gate. Rendered by both
 * the Till and the Manager app-shells whenever the authenticated user carries
 * `mustChangePassword`. Dumb/presentational only — the connecting layer wires
 * `onSubmit` to the changePassword adapter and re-hydrates on success.
 */
export function ChangePassword({ onSubmit, error, busy }) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [localError, setLocalError] = useState(null);
    const currentRef = useRef(null);
    useEffect(() => {
        currentRef.current?.focus();
    }, []);
    function submit() {
        if (busy)
            return;
        setLocalError(null);
        if (newPassword.length < MIN_LENGTH) {
            setLocalError(t("changePassword.errorMinLength"));
            return;
        }
        if (newPassword !== confirmPassword) {
            setLocalError(t("changePassword.errorMismatch"));
            return;
        }
        void onSubmit(currentPassword, newPassword);
    }
    const shownError = localError ?? error ?? null;
    const disabled = busy || !currentPassword || !newPassword || !confirmPassword;
    return (_jsx("div", { style: {
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--bg)",
            padding: 24,
        }, children: _jsxs("div", { className: "surface shadow-warm", style: { width: "100%", maxWidth: 420, padding: 28 }, children: [_jsxs("div", { style: { textAlign: "center", marginBottom: 20 }, children: [_jsx(AppLogo, { size: "lg", subtitle: "Shop operations", className: "justify-center" }), _jsx("h1", { style: { fontSize: 18, fontWeight: 600, color: "var(--ink-1)", marginTop: 12 }, children: t("changePassword.title") }), _jsx("p", { style: { fontSize: 13, color: "var(--ink-2)", marginTop: 4 }, children: t("changePassword.subtitle") })] }), _jsxs("form", { onSubmit: (e) => {
                        e.preventDefault();
                        submit();
                    }, style: { display: "flex", flexDirection: "column", gap: 12 }, children: [_jsxs("div", { children: [_jsx(Label, { htmlFor: "cp-current", children: t("changePassword.currentPassword") }), _jsx(Input, { ref: currentRef, id: "cp-current", type: "password", autoComplete: "current-password", value: currentPassword, onChange: (e) => setCurrentPassword(e.target.value) })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "cp-new", children: t("changePassword.newPassword") }), _jsx(Input, { id: "cp-new", type: "password", autoComplete: "new-password", value: newPassword, onChange: (e) => setNewPassword(e.target.value) })] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "cp-confirm", children: t("changePassword.confirmPassword") }), _jsx(Input, { id: "cp-confirm", type: "password", autoComplete: "new-password", value: confirmPassword, onChange: (e) => setConfirmPassword(e.target.value) })] }), _jsx(Button, { type: "submit", variant: "accent", size: "lg", className: "w-full", disabled: disabled, children: t("changePassword.submit") })] }), shownError && (_jsxs("div", { style: {
                        marginTop: 14,
                        padding: "10px 12px",
                        borderRadius: 6,
                        background: "var(--danger-soft)",
                        color: "var(--danger)",
                        fontSize: 12,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                    }, children: [_jsx(Icon, { name: "circle-alert", size: "sm" }), shownError] }))] }) }));
}
//# sourceMappingURL=ChangePassword.js.map